import React from 'react'
import { AlertTriangle, Check, ShieldCheck } from 'lucide-react'
import type { ComplianceRuleProfile } from '@/lib/projectsApi'

const MAX_SELECTED_COMPLIANCE_PROFILES = 2

function toggleComplianceProfile(
  selectedCodes: string[],
  profileCode: string,
): string[] {
  if (selectedCodes.includes(profileCode)) {
    return selectedCodes.filter(code => code !== profileCode)
  }
  if (selectedCodes.length >= MAX_SELECTED_COMPLIANCE_PROFILES) return selectedCodes
  return [...selectedCodes, profileCode]
}

function makePrimaryComplianceProfile(
  selectedCodes: string[],
  profileCode: string,
): string[] {
  if (!selectedCodes.includes(profileCode)) return selectedCodes
  return [profileCode, ...selectedCodes.filter(code => code !== profileCode)]
}

interface Props {
  profiles: ComplianceRuleProfile[]
  selectedCodes: string[]
  onChange: (codes: string[]) => void
  disabled?: boolean
}

export const ComplianceProfileSelector: React.FC<Props> = ({
  profiles,
  selectedCodes,
  onChange,
  disabled = false,
}) => {
  const full = selectedCodes.length >= MAX_SELECTED_COMPLIANCE_PROFILES

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {profiles.map(profile => {
          const selectedIndex = selectedCodes.indexOf(profile.profile_code)
          const selected = selectedIndex >= 0
          const blocked = disabled || (!selected && full)
          const positionLabel = selectedIndex === 0 ? 'Primary' : selectedIndex === 1 ? 'Secondary' : null

          return (
            <div
              key={profile.profile_code}
              className={
                'rounded-lg border p-3 transition-colors ' +
                (selected
                  ? 'border-blue-500 bg-blue-50 dark:border-blue-600 dark:bg-blue-900/25'
                  : blocked
                    ? 'border-slate-200 bg-slate-50 opacity-55 dark:border-slate-700 dark:bg-slate-800/40'
                    : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-700')
              }
            >
              <button
                type="button"
                aria-pressed={selected}
                disabled={blocked}
                onClick={() => onChange(toggleComplianceProfile(selectedCodes, profile.profile_code))}
                className="flex w-full items-start gap-2.5 text-left disabled:cursor-not-allowed"
              >
                <span className={
                  'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ' +
                  (selected
                    ? 'border-blue-600 bg-blue-600 text-white'
                    : 'border-slate-300 text-transparent dark:border-slate-600')
                }>
                  <Check className="h-3.5 w-3.5" />
                </span>

                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                      {profile.class_society}
                    </span>
                    {positionLabel && (
                      <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                        {positionLabel}
                      </span>
                    )}
                    <span className="text-[10px] text-slate-400">{profile.edition_label}</span>
                  </span>
                  <span className="mt-0.5 block text-xs font-medium text-slate-600 dark:text-slate-300">
                    {profile.display_name}
                  </span>
                  <span className="mt-0.5 block text-[11px] leading-snug text-slate-500 dark:text-slate-400">
                    {profile.notation_label}
                  </span>
                </span>
              </button>

              <div className="mt-2 flex items-center gap-1.5 border-t border-slate-200/70 pt-2 text-[10px] dark:border-slate-700">
                {profile.authority_status === 'verified' ? (
                  <>
                    <ShieldCheck className="h-3 w-3 text-emerald-500" />
                    <span className="truncate text-slate-500" title={profile.rule_reference}>
                      {profile.rule_reference}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" />
                    <span className="text-amber-700 dark:text-amber-300">
                      Template present; rule reference needs confirmation
                    </span>
                  </>
                )}
                {selectedIndex === 1 && !disabled && (
                  <button
                    type="button"
                    onClick={() => onChange(makePrimaryComplianceProfile(selectedCodes, profile.profile_code))}
                    className="ml-auto shrink-0 font-medium text-blue-600 hover:underline dark:text-blue-400"
                  >
                    Make primary
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Select one or two classes. A dual-class vessel uses one evidence workflow but produces a separate deliverable set for each class.
        {full && ' Remove a class before selecting another.'}
      </p>
    </div>
  )
}
